/**
 * Formatting helpers. Pure, DOM-free, and tested.
 *
 * Every wall-clock time this dashboard shows is **Europe/Bucharest**. The
 * backend stores and returns UTC (`isoUtc`), and Alex reads times in Romania
 * time, so the conversion happens exactly once, here, with `Intl` rather than a
 * fixed offset — a hard-coded +02:00/+03:00 is wrong for half the year.
 */

const BUCHAREST = 'Europe/Bucharest';

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUCHAREST,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * `8 Sep 2026, 21:02` — deliberately NOT all-numeric.
 *
 * `08/09/2026` is two different days depending on which side of the Atlantic
 * the reader learned to write dates, and this dashboard is read at 03:00 by
 * someone who has just been woken up. Everything else here is precise to the
 * minute; the one field that says WHICH DAY must not be the ambiguous one.
 *
 * The month is therefore spelled, which removes the ambiguity outright rather
 * than relying on the reader knowing the locale. The day is NOT zero-padded —
 * `8 Sep` reads as prose, and the numerals that have to line up in a column are
 * the clock times, which stay `2-digit` and tabular.
 *
 * The month is truncated to THREE letters by hand. `en-GB` renders September as
 * `Sept`, and a column of timestamps in which one month is a character wider
 * than the other eleven is exactly the kind of ragged edge tabular figures exist
 * to prevent. Truncating the parts is also locale-proof: an ICU update that
 * changes an abbreviation cannot change the column width.
 */
const dateTimeParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUCHAREST,
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** `8 Sep, 21:02` — the same shape without the year, for chart axes. */
const dayMonthParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUCHAREST,
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function assemble(formatter: Intl.DateTimeFormat, date: Date): string {
  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const year = get('year');
  const head = `${get('day')} ${get('month').slice(0, 3)}${year === '' ? '' : ` ${year}`}`;
  return `${head}, ${get('hour')}:${get('minute')}`;
}

/** `14:32` in Bucharest, or the honest word when the input is not a time. */
export function clockTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unparseable time';
  return timeFormatter.format(date);
}

/** `8 Sep 2026, 14:32` in Bucharest. Unambiguous by construction — see above. */
export function fullTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unparseable time';
  return assemble(dateTimeParts, date);
}

/**
 * `8 Sep, 14:32` — the same unambiguous shape, without the year.
 *
 * For the x axis of a multi-day chart, where the year is the same on every tick
 * and spending eight characters restating it costs a tick label.
 */
export function dayTime(date: Date): string {
  return assemble(dayMonthParts, date);
}

/**
 * `3m ago`, `2h 5m ago`, `just now`.
 *
 * A timestamp in the FUTURE is reported as such rather than clamped to "just
 * now": a future `last_seen` is a clock problem and hiding it is how clock
 * problems survive for weeks.
 */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (iso === null || iso === undefined || iso === '') return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unparseable time';
  const deltaMs = now.getTime() - date.getTime();
  if (deltaMs < -60_000) return `${humanizeMs(-deltaMs)} in the future`;
  if (deltaMs < 45_000) return 'just now';
  return `${humanizeMs(deltaMs)} ago`;
}

/** `2h 5m`, `45m`, `12s`. Deterministic and locale-free. */
export function humanizeMs(ms: number): string {
  if (!Number.isFinite(ms)) return String(ms);
  const abs = Math.abs(Math.round(ms));
  const seconds = Math.floor(abs / 1000);
  if (seconds < 60) return `${String(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(rest)}m`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${String(days)}d` : `${String(days)}d ${String(restHours)}h`;
}

export function humanizeSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return 'unknown';
  return humanizeMs(seconds * 1000);
}

/** Binary bytes. `1.8 GiB`. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return String(bytes);
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  let value = Math.abs(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const sign = bytes < 0 ? '-' : '';
  return `${sign}${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit] ?? 'B'}`;
}

/**
 * Kubernetes quantities (`8000m`, `32Gi`, `1500Mi`) to a number.
 *
 * Returns `null` for anything unrecognised rather than guessing — an
 * allocatable value we cannot parse must show as "unparseable", not as 0.
 */
export function parseQuantity(raw: string): number | null {
  const match = /^(-?\d+(?:\.\d+)?)([a-zA-Z]*)$/.exec(raw.trim());
  if (match === null) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  const suffix = match[2] ?? '';
  const multipliers: Record<string, number> = {
    '': 1,
    m: 1e-3,
    k: 1e3,
    M: 1e6,
    G: 1e9,
    T: 1e12,
    P: 1e15,
    Ki: 1024,
    Mi: 1024 ** 2,
    Gi: 1024 ** 3,
    Ti: 1024 ** 4,
    Pi: 1024 ** 5,
  };
  const multiplier = multipliers[suffix];
  return multiplier === undefined ? null : value * multiplier;
}

/** `usd` with four decimals — agent runs cost fractions of a cent. */
export function formatUsd(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return 'not recorded';
  return `$${usd.toFixed(4)}`;
}
